import { useOrganizationQuery } from 'app/modules/organizations/api/organizationsApiHooks';
import { Pricing } from 'app/types/User';
import cx from 'classnames';
import { constantToDisplayName } from "../../../core/constantToDisplayName";
import { UserRole, UserType, useMeQuery } from 'legoland-sdk/dist/experimental';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FormGroup, Input, RadioGroup, Text, VGrid } from 'tombac';
import isEmail from 'validator/lib/isEmail';
import OrganizationPicker from "../../../core/components/OrganizationPicker/OrganizationPicker";
import useEmailAvailability from "../hooks/useEmailAvailability";
import useInput from "../hooks/useInput";
import useInputValidation from "../hooks/useInputValidation";
import "./UserDetailsForm.css";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const initialState = {
  isValid: false,
  email: '',
  role: UserRole.User,
  type: UserType.Portal,
  pricing: Pricing.OldNetworkModel
};
function findTopLevelOrganization(organization) {
  if (organization.parent && !organization.parent.root) {
    const grandparent = findTopLevelOrganization(organization.parent);
    if (!grandparent) return organization.parent;
    return grandparent;
  }
  return null;
}
function UserDetailsForm(_ref) {
  let {
    defaultValues: defaultValuesProp = initialState,
    hasUserContracts,
    newUser = false,
    onChange,
    wide = false
  } = _ref;
  const meQuery = useMeQuery();
  const creatorRole = meQuery.data.me.role;
  const canSetRole = creatorRole === UserRole.Admin;
  const canSeeRole = creatorRole === UserRole.Support;
  const canSetOrganization = creatorRole !== UserRole.Support;
  const defaultValues = useMemo(() => ({
    ...initialState,
    ...defaultValuesProp
  }), [defaultValuesProp]);
  const [emailInput,, emailPristine] = useInput(defaultValues.email);
  emailInput.value = emailInput.value.trim();
  const emailPatternIsValid = useInputValidation(emailInput, email => isEmail(email));
  const emailIsEmpty = emailPristine === false && emailInput.value.length === 0;
  const [role, setRole] = useState(defaultValues.role);
  const emailAvailable = useEmailAvailability(emailInput.value, emailPatternIsValid);
  const [org, setOrg] = useState({
    id: defaultValues.organization
  });
  const prettyType = type => type.split('_').join(' ');
  const uglyType = type => type.split(' ').join('_');
  const [userType, setUserType] = useState(prettyType(defaultValues.type));
  const [pricing] = useState(defaultValues.pricing);
  const [parentOrg, setParentOrg] = useState(null);
  const [sapIdInput] = useInput('');
  const emailIsValid = newUser ? !emailIsEmpty && emailPatternIsValid : true;
  const orgIsValid = org !== undefined;
  const formIsValid = emailIsValid && orgIsValid && (newUser ? emailAvailable === 'yes' : true);
  useEffect(() => {
    onChange({
      isValid: formIsValid,
      email: emailInput.value,
      role: role,
      organization: org?.id,
      sapId: sapIdInput.value || undefined,
      type: uglyType(userType),
      pricing
    });
  }, [formIsValid, emailInput.value, role, org?.id, userType, pricing]);
  const {
    data: userOrganizationDetails
  } = useOrganizationQuery({
    organizationId: org?.id
  }, {
    enabled: org?.id !== undefined
  });
  useEffect(() => {
    if (!userOrganizationDetails) return;
    setParentOrg(findTopLevelOrganization(userOrganizationDetails));
  }, [userOrganizationDetails]);
  const canSetUserType = creatorRole === UserRole.Admin && role !== UserRole.Distributor;
  const canSeeSapId = creatorRole === UserRole.Admin || creatorRole === UserRole.Support;
  const showTypeField = canSetUserType && role === UserRole.User;
  const className = cx('UserDetailsForm', {
    'UserDetailsForm--no-type': !showTypeField,
    'UserDetailsForm--distributor': creatorRole === UserRole.Distributor,
    'UserDetailsForm--wide': wide,
    'UserDetailsForm--no-email': !newUser
  });
  const emailError = emailAvailable === 'no' ? 'User with this email already exists.' : !emailIsValid ? 'Please enter a valid email.' : undefined;
  const roleOptions = [UserRole.User, UserRole.Distributor, UserRole.Support, UserRole.Admin];
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      className: className,
      children: [canSetRole ? /*#__PURE__*/_jsx(FormGroup, {
        htmlFor: "user-role-select",
        label: "Role",
        style: {
          gridArea: 'role'
        },
        children: /*#__PURE__*/_jsx(RadioGroup, {
          getOptionLabel: constantToDisplayName,
          getOptionValue: option => option,
          id: "user-role-select",
          options: roleOptions,
          onChange: setRole,
          value: role,
          variant: "horizontal"
        })
      }) : canSeeRole && /*#__PURE__*/_jsx(FormGroup, {
        label: "Role",
        style: {
          gridArea: 'role'
        },
        children: /*#__PURE__*/_jsx(Text, {
          children: constantToDisplayName(role)
        })
      }), !newUser && parentOrg && /*#__PURE__*/_jsx(FormGroup, {
        htmlFor: "user-role-select",
        label: "Parent organization",
        style: {
          gridArea: 'parent-org'
        },
        children: /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(ParentOrganizationLink, {
            to: `/users?search=${parentOrg.sapId}&role=DISTRIBUTOR`,
            children: parentOrg.name
          })
        })
      }), /*#__PURE__*/_jsx("div", {
        style: {
          gridArea: 'orgs'
        },
        children: /*#__PURE__*/_jsx(OrganizationPicker, {
          caption: hasUserContracts || !canSetOrganization ? ' ' : undefined,
          disabled: hasUserContracts || !canSetOrganization,
          onOrganizationDetails: setOrg,
          value: org?.id,
          "data-sentry-element": "OrganizationPicker",
          "data-sentry-source-file": "UserDetailsForm.tsx",
          "data-sentry-element": "OrganizationPicker"
        })
      }), canSeeSapId && (org?.sapId === undefined || org.sapId === null ? org?.id !== undefined && newUser && /*#__PURE__*/_jsx(FormGroup, {
        label: "SAP ID",
        style: {
          gridArea: 'sap-id'
        },
        children: /*#__PURE__*/_jsx(Input, {
          id: "sapid-input",
          $w: "100%",
          ...sapIdInput
        })
      }) : /*#__PURE__*/_jsx("div", {
        style: {
          gridArea: 'sap-id'
        },
        children: /*#__PURE__*/_jsx(FormGroup, {
          label: "SAP ID",
          children: /*#__PURE__*/_jsx(Text, {
            $alignItems: "center",
            $display: "flex",
            $height: "5sp",
            as: "span",
            children: org.sapId
          })
        })
      })), newUser && /*#__PURE__*/_jsx(FormGroup, {
        error: emailError,
        htmlFor: "email-input",
        label: "Email",
        style: {
          gridArea: 'email'
        },
        children: /*#__PURE__*/_jsx(Input, {
          id: "email-input",
          invalid: !!emailError,
          type: "email",
          $w: "100%",
          ...emailInput
        })
      }), showTypeField && /*#__PURE__*/_jsx(FormGroup, {
        htmlFor: "user-type-select",
        label: "Type",
        style: {
          gridArea: 'type'
        },
        children: /*#__PURE__*/_jsx(VGrid, {
          gap: 3,
          children: /*#__PURE__*/_jsx(RadioGroup, {
            getOptionLabel: constantToDisplayName,
            getOptionValue: option => option,
            id: "user-type-select",
            options: [UserType.Portal, UserType.ApiOnly].map(prettyType),
            onChange: userType => setUserType(userType),
            value: userType,
            variant: "horizontal",
            disabled: hasUserContracts
          })
        })
      })]
    })
  });
}
export default UserDetailsForm;
const ParentOrganizationLink = styled(Link).withConfig({
  displayName: "UserDetailsForm__ParentOrganizationLink"
})(["font-weight:bold;text-decoration:underline;"]);